import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

export function useUserLists() {
  const { currentUser } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLists = useCallback(async () => {
    if (!currentUser?.email) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('user_call_lists')
        .select('*, user_call_list_leads(count)')
        .eq('user_email', currentUser.email.toLowerCase())
        .order('created_at', { ascending: false });
      if (err) throw err;
      setLists(
        (data || []).map(list => ({
          ...list,
          lead_count: list.user_call_list_leads?.[0]?.count || 0
        }))
      );
    } catch (err) {
      logger.error('Error fetching user lists:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const createList = useCallback(async (name, description = '') => {
    if (!currentUser?.email) throw new Error('Not authenticated');
    const { data, error: err } = await supabase
      .from('user_call_lists')
      .insert({ user_email: currentUser.email.toLowerCase(), name: name.trim(), description: description.trim() })
      .select()
      .single();
    if (err) throw err;
    await fetchLists();
    return data;
  }, [currentUser?.email, fetchLists]);

  const addLeadsToList = useCallback(async (listId, leads) => {
    const rows = leads.map(lead => ({
      list_id: listId,
      lead_id: String(lead.id),
      lead_email: lead.email
    }));
    const { error: err } = await supabase
      .from('user_call_list_leads')
      .upsert(rows, { onConflict: 'list_id,lead_email', ignoreDuplicates: true });
    if (err) throw err;
    await fetchLists();
  }, [fetchLists]);

  const getListLeads = useCallback(async (listId) => {
    const { data, error: err } = await supabase
      .from('user_call_list_leads')
      .select('*')
      .eq('list_id', listId)
      .order('added_at', { ascending: false });
    if (err) throw err;
    return data || [];
  }, []);

  const removeLeadsFromList = useCallback(async (listId, leadEmails) => {
    const { error: err } = await supabase
      .from('user_call_list_leads')
      .delete()
      .eq('list_id', listId)
      .in('lead_email', leadEmails);
    if (err) throw err;
    await fetchLists();
  }, [fetchLists]);

  const deleteList = useCallback(async (listId) => {
    const { error: err } = await supabase
      .from('user_call_lists')
      .delete()
      .eq('id', listId)
      .eq('user_email', currentUser.email.toLowerCase());
    if (err) throw err;
    await fetchLists();
  }, [currentUser?.email, fetchLists]);

  const updateList = useCallback(async (listId, updates) => {
    const { error: err } = await supabase
      .from('user_call_lists')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', listId)
      .eq('user_email', currentUser.email.toLowerCase());
    if (err) throw err;
    await fetchLists();
  }, [currentUser?.email, fetchLists]);

  // Get ALL lead emails across ALL of this user's lists (for hiding from Call List)
  const getAllUserListLeadEmails = useCallback(async () => {
    if (!currentUser?.email) return [];
    // Get all list IDs for this user
    const listIds = lists.map(l => l.id);
    if (listIds.length === 0) return [];
    const { data, error: err } = await supabase
      .from('user_call_list_leads')
      .select('lead_email')
      .in('list_id', listIds);
    if (err) { logger.error('Error fetching all list lead emails:', err); return []; }
    return [...new Set((data || []).map(r => r.lead_email))];
  }, [currentUser?.email, lists]);

  // Mark a single lead as called inside a list
  const markLeadCalled = useCallback(async (listId, leadEmail) => {
    const { error: err } = await supabase
      .from('user_call_list_leads')
      .update({ call_status: 'called', called_at: new Date().toISOString() })
      .eq('list_id', listId)
      .eq('lead_email', leadEmail);
    if (err) throw err;
  }, []);

  // Bulk mark leads as called
  const markLeadsCalled = useCallback(async (listId, leadEmails) => {
    const { error: err } = await supabase
      .from('user_call_list_leads')
      .update({ call_status: 'called', called_at: new Date().toISOString() })
      .eq('list_id', listId)
      .in('lead_email', leadEmails);
    if (err) throw err;
  }, []);

  // Get all called lead emails across all of this user's lists
  const getCalledLeadEmails = useCallback(async () => {
    if (!currentUser?.email) return [];
    const listIds = lists.map(l => l.id);
    if (listIds.length === 0) return [];
    const { data, error: err } = await supabase
      .from('user_call_list_leads')
      .select('lead_email, called_at')
      .in('list_id', listIds)
      .eq('call_status', 'called');
    if (err) { logger.error('Error fetching called leads:', err); return []; }
    return data || [];
  }, [currentUser?.email, lists]);

  return {
    lists,
    loading,
    error,
    fetchLists,
    createList,
    addLeadsToList,
    getListLeads,
    removeLeadsFromList,
    deleteList,
    updateList,
    getAllUserListLeadEmails,
    markLeadCalled,
    markLeadsCalled,
    getCalledLeadEmails
  };
}
